import { join, interpolate, printf, toText } from "../fable_modules/fable-library-js.4.24.0/String.js";
import { map, tail as tail_1, fold, head as head_1 } from "../fable_modules/fable-library-js.4.24.0/Array.js";
import { empty, singleton, append, delay, toArray } from "../fable_modules/fable-library-js.4.24.0/Seq.js";
import { int32ToString } from "../fable_modules/fable-library-js.4.24.0/Util.js";
import { value as value_1 } from "../fable_modules/fable-library-js.4.24.0/Option.js";

/**
 * Helper function that takes a list of tuples and creates a query string for you.
 */
export function generateQueryString(queryStrings) {
    let first;
    const clo = toText(printf("?%s=%s"));
    first = ((arg) => {
        const clo_1 = clo(arg);
        return clo_1;
    });
    let subsequent;
    const clo_2 = toText(printf("&%s=%s"));
    subsequent = ((arg_2) => {
        const clo_3 = clo_2(arg_2);
        return clo_3;
    });
    if (queryStrings.length <= 0) {
        return "";
    }
    else {
        let head;
        const tupledArg = head_1(queryStrings);
        head = first(tupledArg[0])(tupledArg[1]);
        if (queryStrings.length === 1) {
            return head;
        }
        else {
            return fold((acc, tupledArg_1) => (acc + subsequent(tupledArg_1[0])(tupledArg_1[1])), head, tail_1(queryStrings));
        }
    }
}

export const API_PasswordRecovery_RecoverServer = printf("/api/password-recover/recover/%s");

export function API_Account_Enable(accountId) {
    return "/api/account" + toText(interpolate("/%d%P()/enable", [accountId]));
}

/**
 * /api/company/%i
 */
export function API_Company_Company(id) {
    return "/api/company" + (`/${id}`);
}

/**
 * /api/company/%i/department/%i
 */
export function API_Company_Department(companyId, departmentId) {
    return API_Company_Company(companyId) + toText(printf("/department/%i"))(departmentId);
}

/**
 * /api/customer-group/%i
 */
export function API_CustomerGroup_Id(id) {
    return "/api/customer-group" + (`/${id}`);
}

export function API_CustomerGroup_Disable(id) {
    return `/api/customer-group/${id}`;
}

export function API_CustomerGroup_Favorite(id) {
    return "/api/customer-group" + (`/${id}/favorite`);
}

export function API_CustomerGroup_Orders(id) {
    return "/api/customer-group" + (`/${id}/orders`);
}

export function API_CustomerGroup_OrdersWith(queryStrings, id) {
    return API_CustomerGroup_Orders(id) + generateQueryString(queryStrings);
}

export function API_Customer_FetchAllWith(queryStrings) {
    return "/api/customer" + generateQueryString(queryStrings);
}

/**
 * /api/customer/%i
 */
export function API_Customer_Customer(id) {
    return "/api/customer" + (`/${id}`);
}

export function API_Customer_Disable(groupId) {
    return `/api/customer/${groupId}`;
}

/**
 * /api/customer/%i/responsible
 */
export function API_Customer_Responsible(id) {
    const arg = API_Customer_Customer(id);
    return toText(printf("%s/responsible"))(arg);
}

export function API_Customer_Contact(customerId) {
    return `/api/customer/${customerId}/contact`;
}

export function API_Customer_Orders(id) {
    return API_Customer_Customer(id) + "/orders";
}

/**
 * Should produce: /api/customer/1/orders?with-ticket=true&open=false
 */
export function API_Customer_OrdersWith(queryStrings, id) {
    return API_Customer_Orders(id) + generateQueryString(queryStrings);
}

/**
 * /api/customer/%i/favorite
 */
export function API_Customer_favorite(id) {
    return API_Customer_Customer(id) + "/favorite";
}

export function API_Customer_Agreement(id) {
    return `/api/customer/${id}/agreement`;
}

export const API_Customers_root = "/api/customers";

export const API_Customers_count = API_Customers_root + "/count";

export const API_Localization_Root = "/api/localization";

/**
 * /api/machine/%i/favorite
 */
export function API_Machine_favorite(id) {
    return "/api/machine" + (`/${id}/favorite`);
}

export function API_Machine_Service(machineId, serviceId) {
    return "/api/machine" + (`/${machineId}/services/${serviceId}`);
}

export function API_Machine_Services(machineId) {
    return "/api/machine" + (`/${machineId}/services`);
}

export function API_Machine_ServiceWithTicket(machineId, ticketId) {
    return API_Machine_Services(machineId) + (`?ticket=${ticketId}`);
}

export function API_Machine_CategoryVersion(machineId) {
    return "/api/machine" + (`/${machineId}/category-version`);
}

export function API_Machine_NotesHistory(machineId) {
    return `/api/machine/notes/${machineId}`;
}

export function API_History_CustomerHistory(customerId, year) {
    return `/api/history/customer/${customerId}?year=${year}`;
}

export function API_History_CustomerHistoryWithMachine(customerId, year, machineId) {
    return `/api/history/customer/${customerId}?year=${year}&machineId=${machineId}`;
}

export function API_History_GroupHistory(groupId, year) {
    return `/api/history/group/${groupId}?year=${year}`;
}

function API_Economy_toOrderTypeVar(orderType) {
    if (orderType == null) {
        return "";
    }
    else {
        return `&type=${orderType}`;
    }
}

export function API_Economy_MachineEconomyYear(customerId, machineId, year, orderType) {
    return (`/api/economy/customer/${customerId}/machine/${machineId}/year`) + generateQueryString(toArray(delay(() => append(singleton(["year", int32ToString(year)]), delay(() => ((orderType != null) ? singleton(["type", int32ToString(value_1(orderType))]) : empty()))))));
}

export function API_Economy_MachineEconomyTotal(customerId, machineId, yearStart, yearEnd, orderType) {
    return (`/api/economy/customer/${customerId}/machine/${machineId}/total`) + generateQueryString(toArray(delay(() => append(singleton(["start", int32ToString(yearStart)]), delay(() => append(singleton(["end", int32ToString(yearEnd)]), delay(() => ((orderType != null) ? singleton(["type", int32ToString(value_1(orderType))]) : empty()))))))));
}

export function API_Economy_MachineSpreadYear(year, customerId, machineId) {
    return (`/api/economy/customer/${customerId}/machine/${machineId}/spread`) + generateQueryString([["year", int32ToString(year)]]);
}

export function API_Economy_CustomerEconomyYear(customerId, year, orderType) {
    return `/api/economy/customer-year/${customerId}?year=${year}${API_Economy_toOrderTypeVar(orderType)}`;
}

export function API_Economy_CustomerEconomyTotal(customerId, yearStart, yearEnd, orderType) {
    return (`/api/economy/customer-total/${customerId}`) + generateQueryString(toArray(delay(() => append(singleton(["start", int32ToString(yearStart)]), delay(() => append(singleton(["end", int32ToString(yearEnd)]), delay(() => ((orderType != null) ? singleton(["type", int32ToString(value_1(orderType))]) : empty()))))))));
}

export function API_Economy_CustomerSpreadYear(customerId, year) {
    return `/api/economy/customer-spread-year/${customerId}?year=${year}`;
}

export function API_Economy_GroupEconomyYear(year, orderType, customerIdOpt, groupId) {
    return (`/api/economy/group-year/${groupId}`) + generateQueryString(toArray(delay(() => append(singleton(["year", int32ToString(year)]), delay(() => append((orderType != null) ? singleton(["type", int32ToString(value_1(orderType))]) : empty(), delay(() => ((customerIdOpt != null) ? singleton(["customer", int32ToString(value_1(customerIdOpt))]) : empty()))))))));
}

export function API_Economy_GroupEconomyTotal(yearStart, yearEnd, orderType, customerIdOpt, groupId) {
    return (`/api/economy/group-total/${groupId}`) + generateQueryString(toArray(delay(() => append(singleton(["start", int32ToString(yearStart)]), delay(() => append(singleton(["end", int32ToString(yearEnd)]), delay(() => append((orderType != null) ? singleton(["type", int32ToString(value_1(orderType))]) : empty(), delay(() => ((customerIdOpt != null) ? singleton(["customer", int32ToString(value_1(customerIdOpt))]) : empty()))))))))));
}

export function API_Economy_GroupSpreadYear(year, customerIdOpt, groupId) {
    return (`/api/economy/group-spread-year/${groupId}`) + generateQueryString(toArray(delay(() => append(singleton(["year", int32ToString(year)]), delay(() => ((customerIdOpt != null) ? singleton(["customer", int32ToString(value_1(customerIdOpt))]) : empty()))))));
}

export function API_Document_MultiDownload(ids) {
    return "/api/document/download" + generateQueryString([["files", join(",", map((value) => value, ids))]]);
}

export function API_Document_Document(id) {
    return "/api/document" + (`/${id}`);
}

export function API_Document_Download(id) {
    return API_Document_Document(id) + "/download";
}

export function API_Document_DownloadVersion(version, id) {
    return API_Document_Download(id) + generateQueryString([["version", int32ToString(version)]]);
}

export function API_Document_Version(version, id) {
    return API_Document_Document(id) + (`/version?value=${version}`);
}

export function API_Document_MachineDocuments(machineId) {
    return `/api/document/machine/${machineId}`;
}

/**
 * /api/document/customer/%i{id}
 */
export function API_Document_CustomerDocuments(customerId) {
    return `/api/document/customer/${customerId}`;
}

export function API_Document_CustomerAttachments(queryStrings) {
    return "/api/document/customer-attachment" + generateQueryString(queryStrings);
}

/**
 * /api/document/customer-uploads/00000000-0000-0000-0000-000000000000
 */
export function API_Document_CustomerAttachment(documentGuid) {
    return API_Document_CustomerAttachments([]) + (`/${documentGuid}`);
}

/**
 * /api/document/customer-uploads/00000000-0000-0000-0000-000000000000/category
 */
export function API_Document_CustomerAttachmentCategory(documentGuid) {
    return API_Document_CustomerAttachment(documentGuid) + "/category";
}

/**
 * /api/document/customer-uploads/00000000-0000-0000-0000-000000000000/download
 */
export function API_Document_DownloadCustomerAttachment(documentId) {
    return API_Document_CustomerAttachment(documentId) + "/download";
}

export function API_CustomerPending_Reject(rowId) {
    return `/api/pending/${rowId}`;
}

export function API_Dashboard_CustomerUsage(customerId) {
    return `/api/dashboard/customer-usage/${customerId}`;
}

export function API_Tickets_GroupTickets(groupId, includeClosed, responsibleOnly) {
    return toText(interpolate("/api/tickets/group/%i%P()?closed=%b%P()&responsible-only=%b%P()", [groupId, includeClosed, responsibleOnly]));
}

export function API_Tickets_CustomerTickets(customerId, includeClosed, responsibleOnly) {
    return toText(interpolate("/api/tickets/customer/%i%P()?closed=%b%P()&responsible-only=%b%P()", [customerId, includeClosed, responsibleOnly]));
}

/**
 * /api/tickets/customer/{id}?closed={includeClosed}&order={hasOrder}
 */
export function API_Tickets_CustomerTicketsWithOrders(customerId, includeClosed, responsibleOnly, hasOrder) {
    return API_Tickets_CustomerTickets(customerId, includeClosed, responsibleOnly) + toText(interpolate("&with-order=%b%P()", [hasOrder]));
}

export function API_Tickets_MachineTickets(machineId, includeClosed, responsibleOnly) {
    return toText(interpolate("/api/tickets/machine/%i%P()?closed=%b%P()&responsible-only=%b%P()", [machineId, includeClosed, responsibleOnly]));
}

export function API_Tickets_Tickets(includeClosed, responsibleOnly) {
    return toText(interpolate("/api/tickets?closed=%b%P()&responsible-only=%b%P()", [includeClosed, responsibleOnly]));
}

/**
 * For GET, PUT AND DELETE
 */
export function API_Tickets_Ticket(ticketId) {
    return "/api/tickets" + (`/${ticketId}`);
}

/**
 * /api/tickets/%i{ticketId}/audit-logs
 */
export function API_Tickets_TicketAuditLogs(ticketId) {
    return API_Tickets_Ticket(ticketId) + "/audit-logs";
}

/**
 * <c>/api/tickets/time/{entryId}</c>
 */
export function API_Tickets_DeleteTimeEntry(entryId) {
    return `/api/tickets/time/${entryId}`;
}

/**
 * <c>/api/tickets/{id}/order</c>
 */
export function API_Tickets_Order(id) {
    return API_Tickets_Ticket(id) + "/order";
}

export const API_Orders_Root = "/api/orders";

export function API_Orders_With(queryStrings) {
    return API_Orders_Root + generateQueryString(queryStrings);
}

export function API_Orders_Order(id) {
    return API_Orders_Root + (`/${id}`);
}

export function API_Orders_VismaSearch(companyId, customerId, vismaNumber) {
    return (API_Orders_Root + "/vismasearch") + generateQueryString([["company-id", int32ToString(companyId)], ["customer-id", int32ToString(customerId)], ["visma-order-number", vismaNumber]]);
}

